import { Dispatch, SetStateAction, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import { SATOSPortal } from "@/DataAdapter/SATOSPortal";

import { Checklist } from "@/model/Checklist/Checklist";
import { ChecklistStepLog } from "@/model/Checklist/ChecklistStep";
import { PagedResponse } from "@/model/PagedResponse";
import { ActiveProcedure } from "@/model/Procedure/ActiveProcedure";
import { Procedure } from "@/model/Procedure/Procedure";
import { ProcedureAccordionMoveDirection } from "@/model/Procedure/ProcedureAccordionMoveDirection";
import { ProcedureDetails } from "@/model/Procedure/ProcedureDetail";
import {
	ActiveParentChildNodes,
	ProcedureDetailNode,
} from "@/model/Procedure/ProcedureDetailNode";
import { ProcedureFolderNode } from "@/model/Procedure/ProcedureFolderNode";
import { ProcedureGroup } from "@/model/Procedure/ProcedureGroup";
import { ProcedureMetaData } from "@/model/Procedure/ProcedureMetaData";
import { UserContext } from "@/model/User/UserContext";

import { RouteParams, useLocalStorage } from "@/service/Utilities";

export class ProcedureQueryKeys {
	static readonly procedureKnowledge = "procedureKnowledge";
	static readonly active = "active";
	static readonly checklist = "checklist";
	static readonly data = "data";
	static readonly dependencies = "dependencies";
	static readonly details = "details";
	static readonly document = "document";
	static readonly edit = "edit";
	static readonly metaData = "metaData";
	static readonly filterFolderEqCurrentOrderByNameAsc =
		"?$filter=Folder eq 'Current'&$orderby=Name asc";
	static readonly getupdatedchecklist = "getupdatedchecklist";
	static readonly groupFolders = "groupfolders";
	static readonly groupdetails = "groupdetails";
	static readonly log = "log";
	static readonly materialHandlingChecklistForEdit = "materialhandlingforedit";
	static readonly measurements = "measurements";
	static readonly node = "node";
	static readonly nodediagram = "nodediagram";
	static readonly nodediagramtemplate = "nodediagramtemplate";
	static readonly nodediagramtemplatelist = "nodediagramtemplatelist";
	static readonly orderByNameAsc = "?$orderby=Name asc";
	static readonly procedure = "procedure";
	static readonly procedureDetails = "procedureDetails";
	static readonly procedureGroups = "proceduregroups";
	static readonly procedureGroup = "procedureGroup";
	static readonly procedureNode = "procedureNode";
	static readonly procedureMetaData = "procedureMetaData";
	static readonly procedures = "procedures";
	static readonly search = "search";
	static readonly shift = "shift";
	static readonly status = "status";
	static readonly step = "step";
	static readonly updatechecklist = "updatechecklist";
	static readonly fileMerge = "fileMerge";
	static readonly importPlanFromWizard = "importPlanFromWizard";
	static readonly NodeDiagramTemplate = "NodeDiagramTemplate";
}

export const useFetchProcedures = (filter: string) => {
	return useQuery<Procedure[], any>({
		queryKey: [ProcedureQueryKeys.procedures, filter],
		queryFn: () => SATOSPortal.GetProcedures(`list/${filter}`),
	});
};

export const useFetchActiveProcedures = (
	page: number,
	pageSize: number,
	search?: string,
	sort?: "ASC" | "DESC",
) => {
	return useQuery<PagedResponse<ActiveProcedure>, any>({
		queryKey: [
			ProcedureQueryKeys.procedures,
			ProcedureQueryKeys.active,
			{ page, pageSize, search, sort },
		],
		queryFn: () =>
			SATOSPortal.GetActiveProcedures(page, pageSize, search, sort),
		refetchInterval: 10000,
		placeholderData: (previousData) => previousData,
	});
};

export const useFetchProcedureGroups = (filter: string) => {
	const user = useContext(UserContext);
	const companyId = user?.CompanyId;

	return useQuery<ProcedureGroup[], any>({
		queryKey: [ProcedureQueryKeys.procedureGroups, companyId, filter],
		queryFn: () => SATOSPortal.GetProcedureGroups(filter),
	});
};

export const useFetchProcedureKnowledgeStatus = () => {
	const { id } = useParams<RouteParams>();
	const procedureId = parseInt(id);

	return useQuery<any, any>({
		queryKey: [ProcedureQueryKeys.procedureKnowledge, procedureId],
		queryFn: () => SATOSPortal.GetProcedureKnowledgeStatus(procedureId),
	});
};

export const useFetchActiveProcedureDetails = (idProp?: any) => {
	const { id } = useParams<RouteParams>();

	const procedureId = parseInt(idProp ? idProp : id);

	return useQuery<ProcedureDetails, any>({
		queryKey: [ProcedureQueryKeys.procedureDetails, procedureId],
		queryFn: () => SATOSPortal.GetProcedureDetails(procedureId),
		enabled: !!procedureId,
	});
};

export const useFetchProcedureNode = (procedureId: number, nodeId: number) => {
	return useQuery<ProcedureDetails, any>({
		queryKey: [ProcedureQueryKeys.procedureNode, procedureId, nodeId],
		queryFn: () => SATOSPortal.GetNode(procedureId, nodeId),
		enabled: !!procedureId && !!nodeId,
		refetchInterval: 10000, // TODO: Remove when proper optimistic update is in place using react-query currentV+1
	});
};

export const useFetchReviewChecklist = (
	procedureIdParam: string | undefined,
	nodeId: number | undefined,
	checklistId: number | undefined,
	isUpdatedChecklist: boolean,
) => {
	const procedureId = procedureIdParam
		? parseInt(procedureIdParam)
		: checklistId;

	return useQuery<Checklist, any>({
		queryKey: [
			ProcedureQueryKeys.updatechecklist,
			procedureId,
			nodeId,
			checklistId,
			isUpdatedChecklist,
		],
		queryFn: () =>
			SATOSPortal.GetReviewChecklist(
				procedureId,
				isUpdatedChecklist,
				nodeId,
				checklistId,
			),
		enabled: !!procedureId && (!!nodeId || !!checklistId),
		refetchInterval: 3000,
	});
};

export const useFetchActiveProcedure = (id?: number) => {
	const { id: idParam } = useParams<RouteParams>();
	let procedureId = id;

	if (procedureId === undefined) {
		procedureId = parseInt(idParam);
	}

	return useQuery<Procedure, any>({
		queryKey: [ProcedureQueryKeys.procedure, procedureId],
		queryFn: () => SATOSPortal.GetProcedure(procedureId),
		enabled: !!procedureId,
	});
};

export const useFetchProcedureStepLog = (
	showStepLog: boolean,
	checklistId: number | undefined,
	stepId: number,
) => {
	const { id } = useParams<RouteParams>();

	return useQuery<ChecklistStepLog, any>({
		queryKey: [
			ProcedureQueryKeys.procedureDetails,
			ProcedureQueryKeys.checklist,
			ProcedureQueryKeys.step,
			ProcedureQueryKeys.log,
			id,
			checklistId,
			stepId,
		],
		queryFn: () => SATOSPortal.GetProcedureStepLog(id, checklistId, stepId),
		enabled: !!(showStepLog && checklistId && stepId),
	});
};

export const useFetchSuggestionsByChecklistId = (
	checklistId: number | undefined,
) => {
	return useQuery<ChecklistStepLog, any>({
		queryKey: [
			ProcedureQueryKeys.procedureDetails,
			ProcedureQueryKeys.checklist,
			ProcedureQueryKeys.step,
			ProcedureQueryKeys.log,
			checklistId,
		],
		queryFn: () => SATOSPortal.GetProcedureSuggestionByChecklistId(checklistId),
		enabled: !!checklistId,
	});
};

// Helper function to get the first available node or child from procedure nodes
const getFirstAvailableNodeOrChild = (
	nodes: ProcedureDetailNode[],
): { node: ProcedureDetailNode | undefined; isLeaf: boolean } => {
	// First check if there's a leaf node (node without children)
	const firstLeafNode = nodes.find(
		(node) => !node.Children || node.Children.length === 0,
	);
	if (firstLeafNode) {
		return { node: firstLeafNode, isLeaf: true };
	}

	// Otherwise, find the first node with children
	const firstNodeWithChildren = nodes.find(
		(node) => node.Children && node.Children.length > 0,
	);
	if (firstNodeWithChildren?.Children[0]) {
		return { node: firstNodeWithChildren.Children[0], isLeaf: false };
	}

	return { node: undefined, isLeaf: false };
};

export const useHandleProcedureDetails = (
	nodes: ProcedureDetailNode[] | undefined,
	checklistId?: number,
): ActiveParentChildNodes => {
	// Early return if no nodes
	if (!nodes || nodes.length === 0) {
		return { activeChildNode: undefined, activeParentNode: undefined };
	}
	let activeParentNode: ProcedureDetailNode | undefined;
	let activeChildNode: ProcedureDetailNode | undefined;

	if (checklistId) {
		// Find parent node that contains the child with this schemeId
		const result = findNodeByTreeNodeNumber(nodes, checklistId);
		activeParentNode = result.activeParentNode;
		activeChildNode = result.activeChildNode;
	} else {
		// No schemeId provided, find the first active node based on status
		const result = findActiveNode(nodes);
		activeParentNode = result.activeParentNode;
		activeChildNode = result.activeChildNode;
	}

	// If no active child found, use the first available node or child as fallback
	if (!activeChildNode && !activeParentNode) {
		const fallback = getFirstAvailableNodeOrChild(nodes);
		if (fallback.isLeaf) {
			// For leaf nodes, the node itself is the parent, no child
			activeParentNode = fallback.node;
			activeChildNode = undefined;
		} else {
			// For nodes with children, find the parent of this child
			activeChildNode = fallback.node;
			if (activeChildNode) {
				activeParentNode = nodes.find(
					(node) => node.Children && node.Children.includes(activeChildNode!),
				);
			}
		}
	}

	return { activeChildNode, activeParentNode };
};

const findNodeByTreeNodeNumber = (
	nodes: Array<ProcedureDetailNode>,
	checklistId: number,
): ActiveParentChildNodes => {
	// First check if any node itself has the matching SchemeId (for leaf nodes)
	const leafNode = nodes.find((node) => node.SchemeId === checklistId);
	if (leafNode && (!leafNode.Children || leafNode.Children.length === 0)) {
		// This is a leaf node that matches - return it as the parent with no child
		return { activeParentNode: leafNode, activeChildNode: undefined };
	}

	// Original logic: find parent node that contains the child with this schemeId
	const activeParentNode = nodes.find(
		(node) =>
			node.Children &&
			node.Children.find((child) => child.SchemeId === checklistId),
	);

	const activeChildNode =
		activeParentNode &&
		activeParentNode.Children &&
		activeParentNode.Children.find((child) => child.SchemeId === checklistId);

	return { activeParentNode, activeChildNode };
};

const findActiveNode = (
	procedureDetailNodes: Array<ProcedureDetailNode>,
): ActiveParentChildNodes => {
	// First check for leaf nodes that are active
	const activeLeafNode = procedureDetailNodes.find(
		(node) =>
			(!node.Children || node.Children.length === 0) &&
			(node.CompletedState === "NotStarted" ||
				node.CompletedState === "Started"),
	);

	if (activeLeafNode) {
		// Return the leaf node as parent with no child
		return { activeParentNode: activeLeafNode, activeChildNode: undefined };
	}

	// Original logic: find parent node with active children
	const activeParentNode = procedureDetailNodes.find(
		(node) =>
			node.Children &&
			node.Children.find(
				(child) =>
					child.CompletedState === "NotStarted" ||
					child.CompletedState === "Started",
			),
	);

	const activeChildNode =
		activeParentNode &&
		activeParentNode.Children &&
		activeParentNode.Children.find(
			(child) =>
				child.CompletedState === "NotStarted" ||
				child.CompletedState === "Started",
		);

	return { activeParentNode, activeChildNode };
};

export const sortGroupFolders = (
	a: ProcedureGroup,
	b: ProcedureGroup,
): number => {
	if (a.SortNumber === 0 && b.SortNumber === 0) {
		return a.Name.localeCompare(b.Name);
	}

	if (a.SortNumber === 0) {
		return 1;
	}

	if (b.SortNumber === 0) {
		return -1;
	}

	return a.SortNumber - b.SortNumber;
};

export const useFetchActiveProcedureGroupFolders = (): {
	groupList: ProcedureGroup[];
	setGroupList: Dispatch<SetStateAction<ProcedureGroup[]>>;
	closeAllProcedureGroupFolders: () => void;
	openAllProcedureGroupFolders: () => void;
} => {
	const [groupListLocalStorage, setGroupListLocalStorage] = useLocalStorage<
		ProcedureGroup[]
	>("procedureFoldersState", []);

	const { data: groups } = useFetchProcedureGroups(
		ProcedureQueryKeys.orderByNameAsc,
	);

	useEffect(() => {
		if (!groupListLocalStorage || groupListLocalStorage.length === 0) {
			groups &&
				setGroupListLocalStorage(groups.sort((a, b) => sortGroupFolders(a, b)));
		}

		if (groups && groupListLocalStorage) {
			if (groupListLocalStorage.length !== groups.length) {
				setGroupListLocalStorage(groups);
			} else {
				const sortedGroups = groups.sort((a, b) => sortGroupFolders(a, b));
				for (let i = 0; i < sortedGroups.length; i++) {
					if (
						sortedGroups[i].Id !== groupListLocalStorage[i].Id ||
						sortedGroups[i].Name !== groupListLocalStorage[i].Name ||
						sortedGroups[i].ParentId !== groupListLocalStorage[i].ParentId
					) {
						setGroupListLocalStorage(sortedGroups);
						return;
					}
				}
			}
		}
	}, [groups]);

	const closeAllProcedureGroupFolders = () => {
		setGroupListLocalStorage([
			...groupListLocalStorage.map((group) => {
				group.Expanded = false;
				return group;
			}),
		]);
	};

	const openAllProcedureGroupFolders = () => {
		setGroupListLocalStorage([
			...groupListLocalStorage.map((group) => {
				group.Expanded = true;
				return group;
			}),
		]);
	};

	return {
		groupList: groupListLocalStorage,
		setGroupList: setGroupListLocalStorage,
		closeAllProcedureGroupFolders,
		openAllProcedureGroupFolders,
	};
};

export const ReorderAccordion = (
	procedureDetails: ProcedureDetails | undefined,
	nodeToMove: ProcedureDetailNode,
	Direction: ProcedureAccordionMoveDirection,
) => {
	if (!procedureDetails) {
		return procedureDetails;
	}
	let TargetTreId: number | undefined = 0;
	const node = { ...nodeToMove };
	let OtherNode: ProcedureDetailNode | undefined;

	if (node.TreeParentId === 0) {
		if (Direction === "Up")
			if (node.TreeId > 1) {
				OtherNode = procedureDetails.ProcedureDetailNodes.find(
					(x) => x.TreeId === node.TreeId - 1,
				);
				if (OtherNode?.TreeId) {
					TargetTreId = OtherNode?.TreeId;
				}
			}

		if (Direction === "Down") {
			OtherNode = procedureDetails.ProcedureDetailNodes.find(
				(x) => x.TreeId === node.TreeId + 1,
			);
			if (OtherNode) TargetTreId = OtherNode.TreeId;
		}

		if (TargetTreId > 0 && OtherNode) {
			OtherNode.TreeId = node.TreeId;
			node.TreeId = TargetTreId;
			const OtherNodeBeforeId = OtherNode.NodeBeforeId;
			OtherNode.NodeBeforeId = node.NodeBeforeId;
			node.NodeBeforeId = OtherNodeBeforeId;
			const OtherNodeAfterId = OtherNode.NodeAfterId;
			OtherNode.NodeAfterId = node.NodeAfterId;
			node.NodeAfterId = OtherNodeAfterId;
			procedureDetails.ProcedureDetailNodes.sort(function (a, b) {
				return a.TreeId - b.TreeId;
			});
		}
	} else {
		const RootNode = procedureDetails.ProcedureDetailNodes.find(
			(x) => x.TreeNodeNumber === node.TreeParentId,
		);

		if (Direction === "Up")
			if (node?.NodeBeforeId && node.NodeBeforeId > 0) {
				OtherNode = RootNode?.Children.find(
					(x) => x.TreeId === node.NodeBeforeId,
				);
				TargetTreId = OtherNode?.TreeId;
			} else if (RootNode?.NodeBeforeId && RootNode.NodeBeforeId > 0) {
				OtherNode = RootNode.Children.find(
					(x) => x.TreeId === node.NodeBeforeId,
				);
				if (OtherNode) {
					OtherNode.NodeAfterId = undefined;
				}
				RootNode.Children.shift();

				const OtherRootNode = procedureDetails.ProcedureDetailNodes.find(
					(x) => x.TreeId === RootNode.NodeBeforeId,
				);

				if (OtherRootNode) {
					OtherRootNode.Children.push(node);
					OtherNode = OtherRootNode.Children[OtherRootNode.Children.length];
					OtherNode.NodeAfterId = OtherNode.TreeId + 1;
					node.NodeBeforeId = OtherNode.TreeId;
					node.TreeId = OtherNode.NodeAfterId;
					node.NodeAfterId = undefined;
				}
			}

		if (Direction === "Down")
			if (node.NodeAfterId && node.NodeAfterId > 0) {
				OtherNode = RootNode?.Children.find(
					(x) => x.TreeId === node.NodeAfterId,
				);
				TargetTreId = OtherNode?.TreeId;
			} else if (RootNode?.NodeAfterId && RootNode.NodeAfterId > 0) {
				const OtherRootNode = procedureDetails.ProcedureDetailNodes.find(
					(x) => x.SchemeId === RootNode.NodeAfterId,
				);
				RootNode.Children.pop();
				OtherNode = RootNode.Children.find(
					(x) => x.TreeId === node.NodeBeforeId,
				);

				if (OtherNode) {
					OtherNode.NodeAfterId = undefined;
				}
				let x = 1;
				OtherRootNode?.Children.unshift(node);
				OtherRootNode?.Children.map((child) => {
					if (x === 1) child.NodeBeforeId = undefined;
					else child.NodeBeforeId = x - 1;
					child.TreeId = x;
					if (child.NodeAfterId != null) child.NodeAfterId = x + 1;
					x++;
				});
			}

		if (TargetTreId && TargetTreId > 0) {
			if (OtherNode) {
				OtherNode.TreeId = node.TreeId;
				node.TreeId = TargetTreId;
				const OtherNodeBeforeId = OtherNode.NodeBeforeId;
				OtherNode.NodeBeforeId = node.NodeBeforeId;
				node.NodeBeforeId = OtherNodeBeforeId;
				const OtherNodeAfterId = OtherNode.NodeAfterId;
				OtherNode.NodeAfterId = node.NodeAfterId;
				node.NodeAfterId = OtherNodeAfterId;
			}
			procedureDetails.ProcedureDetailNodes.sort(function (a, b) {
				return a.TreeId - b.TreeId;
			});
		}
	}
	return procedureDetails;
};

export const useProcedureGroupFolders = (companyId: number | undefined) => {
	return useQuery({
		queryKey: [
			ProcedureQueryKeys.procedure,
			ProcedureQueryKeys.groupFolders,
			companyId,
		],
		queryFn: () => SATOSPortal.GetProcedureMetaDataFolders(companyId),
		enabled: !!companyId,
	});
};

export const GetDocumentsUsed = (companyId: number | undefined) => {
	return useQuery({
		queryKey: [ProcedureQueryKeys.document, companyId],
		queryFn: () => SATOSPortal.GetDocumentsUsed(),
		enabled: !!companyId,
	});
};

export const useFetchDocumentForCurrentNode = (request: {
	ProcedureId: number;
	SchemeId: number;
}) => {
	return useQuery({
		queryKey: [
			ProcedureQueryKeys.document,
			ProcedureQueryKeys.node,
			request.SchemeId,
		],
		queryFn: () => SATOSPortal.GetNodeDocument(request),
		enabled: !!request.SchemeId,
	});
};

export const useGetUpdatedProcedures = (companyId: number | undefined) => {
	return useQuery({
		queryKey: [ProcedureQueryKeys.procedure, companyId],
		queryFn: () => SATOSPortal.GetUppdatedProcedures(),
		enabled: !!companyId,
	});
};

export const useSearchForProcedureDocument = (search: string) => {
	return useQuery({
		queryKey: [
			ProcedureQueryKeys.procedure,
			ProcedureQueryKeys.document,
			ProcedureQueryKeys.search,
			search,
		],
		queryFn: () => SATOSPortal.GetProcedureDocuments(search),
	});
};

export const useFetchProcedureShiftConfiguration = () => {
	const user = useContext(UserContext);
	return useQuery({
		queryKey: [
			ProcedureQueryKeys.procedure,
			ProcedureQueryKeys.shift,
			user?.CompanyId,
		],
		queryFn: () => SATOSPortal.GetProcedureShiftConfiguration(),
		enabled: !!user?.CompanyId,
	});
};

export const useFetchProcedureGroupDetails = (id: number | undefined) => {
	const user = useContext(UserContext);
	return useQuery({
		queryKey: [ProcedureQueryKeys.groupdetails, id, user?.CompanyId],
		queryFn: () => SATOSPortal.GetProcedureGroupDetails(id),
		enabled: !!id,
	});
};

export const useFetchProcedureMetaData = (filter: string) => {
	const abortController = new AbortController();

	const response = useQuery<ProcedureMetaData, any>({
		queryKey: [ProcedureQueryKeys.procedureMetaData],
		queryFn: () =>
			SATOSPortal.ProcedureMetaData(filter, abortController.signal),
	});
	if (response && response.isFetched) {
		if (!response.data?.Folders.find((folder) => folder.Name === "[Root]")) {
			const rootnode: ProcedureFolderNode = {
				GroupNumber: 0,
				Level: 0,
				Name: "[Root]",
				Parent: 0,
			};
			response.data?.Folders.unshift(rootnode);
		}
	}

	return response;
};
